import { renderBlocks } from '@/components/blocks';
import { Layout } from '@/components/layout/Layout';
import { BASE_REVALIDATE } from '@/lib/constants';
import { extractPolicyPaths } from '@/lib/extractPolicyPaths';
import {
  allPoliciesQuery,
  navigationCollectionQuery,
  policyQuery,
  siteSettingsQuery,
} from '@/lib/queries';
import { getClient, readToken } from '@/lib/sanityUtils';
import { getValidLocale } from '@/lib/utils';

import type { SanityPolicyType } from '@/types/sanity';
import type { Navigation_exType, SiteSettings } from '@/types/shared';
import type {
  GetStaticPaths,
  GetStaticProps,
  InferGetStaticPropsType,
} from 'next';

export const getStaticPaths = (async () => {
  const allPolicies =
    await getClient().fetch<SanityPolicyType[]>(allPoliciesQuery());

  return { paths: extractPolicyPaths(allPolicies), fallback: 'blocking' };
}) satisfies GetStaticPaths;

export const getStaticProps = (async ({
  params: { slug },
  locale,
  draftMode = false,
}) => {
  const client = getClient(draftMode ? { token: readToken } : undefined);

  const validLocale = getValidLocale(locale);

  if (!validLocale) {
    return {
      notFound: true,
    };
  }

  const policy = await client.fetch<SanityPolicyType>(policyQuery({ slug }));

  if (!policy) {
    return {
      notFound: true,
    };
  }

  const siteSettings = await client.fetch<SiteSettings>(
    siteSettingsQuery({ locale: validLocale }),
  );

  const navigationCollection = await client.fetch<Navigation_exType[]>(
    navigationCollectionQuery({ locale: validLocale }),
  );

  return {
    props: {
      slug,
      policy,
      siteSettings,
      locale: validLocale,
      draftMode,
      navigationCollection,
      token: draftMode ? readToken : '',
    },
    revalidate: Number(process.env.REVALIDATE || BASE_REVALIDATE),
  };
}) satisfies GetStaticProps;

const Policy = ({
  policy,
  siteSettings,
  draftMode,
  navigationCollection,
}: InferGetStaticPropsType<typeof getStaticProps>) => {
  return (
    <Layout
      SEO={policy?.SEO}
      siteSettings={siteSettings}
      preview={draftMode}
      navigationCollection={navigationCollection}
      dynamicYield={{
        contextPageType: 'OTHER',
      }}
    >
      {renderBlocks(policy?.blocks)}
    </Layout>
  );
};

export default Policy;
